import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Form, FormikProvider, useFormik } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import { makeStyles } from '@mui/styles';
import { LoadingButton } from '@mui/lab';
import { ApplyFotaVersion } from '../../../network/Fota';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: { xs: '70%', md: '30%' },
  maxWidth: { xs: '70%', md: '30%' },
  bgcolor: 'background.paper',
  p: 2,
  borderRadius: 1,
  maxHeight: '90vh',
  overflowY: 'auto'
};

const useStyles = makeStyles((theme) => ({
  menuItem: {
    fontSize: '14px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '12px'
    },
    minHeight: '20px',
    borderRadius: '8px',
    marginInline: theme.spacing(1)
  }
}));

const fotaVersionSchema = Yup.object().shape({
  versionId: Yup.string().required('Required')
});

export default function SelectFotaVersionToApply({
  deviceData,
  openAssignedModal,
  handleClose,
  versionList,
  onAction,
  setAlertMetas,
  alertMetas,
  selectedIds,
  setSelectedIds,
  setSelectedDeviceData
}) {
  const classes = useStyles();

  const addFotaVersionFormik = useFormik({
    enableReinitialize: true, // This enables the form to reset when initialValues change
    initialValues: {
      deviceId: deviceData?.lock_id,
      versionId: ''
    },
    validationSchema: fotaVersionSchema,
    onSubmit: (values, { resetForm }) => {
      const payload = {
        device_id: deviceData ? [values?.deviceId] : selectedIds,
        version: values?.versionId
      };
      addFotaVersionFormik.isSubmitting = true;
      applyFotaToDevice(payload);
    }
  });

  const { errors, touched, handleSubmit, getFieldProps, setFieldValue, values, isSubmitting } =
    addFotaVersionFormik;

  const applyFotaToDevice = async (data) => {
    try {
      const res = await ApplyFotaVersion(data);
      if (res) {
        addFotaVersionFormik.resetForm();
        addFotaVersionFormik.setSubmitting(false);
        setAlertMetas({
          ...alertMetas,
          severity: 'success',
          snackBarOpened: true,
          msg: 'Fota sent to device Successfully !!'
        });
        onAction();
      }
    } catch (error) {
      addFotaVersionFormik.setSubmitting(false);
      setAlertMetas({
        ...alertMetas,
        severity: 'error',
        snackBarOpened: true,
        msg: error?.response?.data?.validationErrors?.message || 'Something went wrong'
      });
    } finally {
      handleClose();
      setSelectedDeviceData(null);
      setSelectedIds([]);
      setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
    }
  };

  return (
    <Box>
      <Modal
        open={openAssignedModal}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        data-testid="apply-fota-modal"
      >
        <FormikProvider value={addFotaVersionFormik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Box sx={style}>
              <Typography sx={{ fontSize: { xs: 16, md: 18 }, fontWeight: 900, mb: 3 }}>
                Select FOTA version to apply
              </Typography>
              <FormControl fullWidth>
                <InputLabel id="version-select-label" sx={{ fontSize: { xs: '12px', sm: '14px' } }}>
                  Select Version *
                </InputLabel>
                <Select
                  labelId="version-select-label"
                  sx={{
                    fontSize: { xs: '12px', sm: '14px' },
                    '& .MuiOutlinedInput-input': {
                      padding: { xs: '14px 8px', sm: '16.5px 14px' }
                    }
                  }}
                  id="fota-filter-select"
                  label="Select Fota Version*"
                  IconComponent={ExpandMoreIcon}
                  data-testid="version-select-label"
                  {...getFieldProps('versionId')}
                  value={values?.versionId}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 180,
                        overflowY: 'auto'
                      }
                    }
                  }}
                >
                  {versionList?.map((item) => (
                    <MenuItem
                      key={item.id}
                      value={item.id}
                      sx={{ p: 1.5 }}
                      className={classes.menuItem}
                    >
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', mt: 5 }}>
                <Button variant="outlined" size="small" onClick={handleClose}>
                  Cancel
                </Button>
                <LoadingButton
                  variant="contained"
                  type="submit"
                  size="small"
                  data-testid="add-or-save-button"
                  loading={isSubmitting}
                >
                  Apply
                </LoadingButton>
              </Box>
            </Box>
          </Form>
        </FormikProvider>
      </Modal>
    </Box>
  );
}
